import React, { useEffect, useContext, createContext, useRef } from 'react';

type DataFetchTask = Promise<unknown>;

interface DataFetchingContextType {
  registerTask: (task: DataFetchTask) => void;
  isServer: boolean;
}

const DataFetchingContext = createContext<DataFetchingContextType>({
  registerTask: () => {},
  isServer: typeof window === 'undefined',
});

interface DataFetchingProviderProps {
  children: React.ReactNode;
  onAllTasksComplete?: () => void;
  serverTaskRegistrar?: (task: DataFetchTask) => void;
}

export const DataFetchingProvider: React.FC<DataFetchingProviderProps> = ({
  children,
  onAllTasksComplete,
  serverTaskRegistrar,
}) => {
  const tasks = useRef<DataFetchTask[]>([]);
  const isServer = typeof window === 'undefined';

  const registerTask = (task: DataFetchTask) => {
    if (!task) return;

    tasks.current.push(task);

    if (isServer && serverTaskRegistrar) {
      serverTaskRegistrar(task);
    }

    if (!isServer && onAllTasksComplete) {
      Promise.all(tasks.current).then(onAllTasksComplete);
    }
  };

  return (
    <DataFetchingContext.Provider value={{ registerTask, isServer }}>
      {children}
    </DataFetchingContext.Provider>
  );
};

export const useDataFetching = () => useContext(DataFetchingContext);

interface DataFetchProps {
  [key: string]: unknown;
}

interface DataFetchOptions {
  onUpdate?: boolean;
  deps?: (props: DataFetchProps) => unknown[];
}

export function withDataFetching<P extends DataFetchProps>(
  fetchData: (props: P) => DataFetchTask | undefined,
  options: DataFetchOptions = {}
) {
  return (Component: React.ComponentType<P>) => {
    const WithDataFetching = (props: P) => {
      const { registerTask, isServer } = useDataFetching();
      const hasInitialized = useRef(false);

      if (isServer) {
        const task = fetchData(props);
        if (task) {
          registerTask(task);
        }
      }

      const depsArray = options.deps ? options.deps(props) : options.onUpdate ? [props] : [];

      useEffect(() => {
        if (isServer) return;

        if (!hasInitialized.current) {
          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
          hasInitialized.current = true;
          return;
        }

        if (options.onUpdate) {
          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
        } else if (options.deps) {
          /* deps changed after mount — refetch (e.g. SPA query / path change) */
          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
        }
      }, depsArray); // eslint-disable-line react-hooks/exhaustive-deps

      return <Component {...props} />;
    };

    WithDataFetching.displayName = `WithDataFetching(${
      Component.displayName || Component.name || 'Component'
    })`;
    return WithDataFetching;
  };
}
